/**
 * Error normalization for the ceremony API.
 *
 * The backend's public `/ceremony/:uuid` endpoint intentionally returns a
 * generic 404 for BOTH a wrong access key and a nonexistent guest — it's an
 * anti-enumeration measure (see Cermony.postman_collection.json, "Public
 * (Guest) -> Ceremony page"). The two cases are indistinguishable from the
 * client, so this app models 5 error kinds rather than one-per-status-code:
 *
 *   - incomplete    missing/malformed uuid or access_key (checked BEFORE any
 *                    request is sent — never hits the network or the API)
 *   - not_found     401 / 403 / 404 from the API
 *   - rate_limited  429 from the API
 *   - server        5xx, or a non-JSON response (e.g. misconfigured base URL)
 *   - network       fetch itself rejected (offline, DNS, CORS, timeout)
 *
 * UI copy lives here so every consumer shows the same friendly, non-technical
 * message. Never surface a raw status code, backend message, or stack trace.
 *
 * Copy is Arabic — the app's UI language. This is independent of the guest's
 * own name/seat_number, which render with dir="auto" regardless of UI
 * language since that data can be Arabic, English, or mixed.
 */

const COPY = {
  incomplete: {
    title: 'رابط الدعوة غير صالح',
    message: 'رابط هذه الدعوة غير مكتمل.',
  },
  not_found: {
    title: 'تعذّر العثور على الدعوة',
    message: 'لم يعد رابط الدعوة صالحًا، أو تعذّر العثور على الحفل.',
  },
  rate_limited: {
    title: 'محاولات كثيرة جدًا',
    message: 'يرجى الانتظار قليلًا ثم المحاولة مرة أخرى.',
  },
  server: {
    title: 'حدث خطأ ما',
    message: 'نواجه مشكلة في تحميل الدعوة حاليًا. يرجى المحاولة مرة أخرى بعد قليل.',
  },
  network: {
    title: 'مشكلة في الاتصال',
    message: 'نواجه مشكلة في الاتصال بالخادم. يرجى التحقق من اتصالك بالإنترنت والمحاولة مرة أخرى.',
  },
};

export function makeError(kind) {
  const copy = COPY[kind] || COPY.server;
  return { kind, ...copy };
}

/**
 * Map an HTTP status code to an error kind.
 */
export function errorKindFromStatus(status) {
  if (status === 429) return 'rate_limited';
  if (status === 401 || status === 403 || status === 404) return 'not_found';
  if (status >= 500) return 'server';
  return 'server';
}

/** Whether a "Try again" action makes sense for this error kind. */
export function isRetryable(kind) {
  return kind === 'network' || kind === 'server' || kind === 'rate_limited';
}
